<template>
  <BForm>
    <label class="multiselect-label text-mild text-size-14 mr-1">銀行名</label>
    <AppLabel variant="red">必須</AppLabel>
    <Multiselect
      id="id"
      ref="multiselect"
      v-model="inputValues.selectedBank"
      :options="inputValues.searchedBanks"
      value-prop="id"
      label="name"
      placeholder="銀行名を入力して検索できます"
      :limit="100"
      :searchable="true"
      :can-clear="true"
      :can-deselect="false"
      :object="true"
      :search-filter="searchFilter"
      no-results-text="見つかりませんでした"
      no-options-text="1文字以上入力してください"
      @search-change="aliasSearch"
      data-testid="selectedBank"
    />

    <label
      class="multiselect-label text-mild text-size-14 mr-1 bank-branch-select"
      >支店</label
    >
    <AppLabel variant="red">必須</AppLabel>
    <Multiselect
      id="id"
      ref="multiselect"
      v-model="selectedBranch"
      :options="searchedBranches"
      value-prop="id"
      label="name"
      placeholder="支店名・支店コードで検索できます"
      :limit="100"
      :searchable="true"
      :can-clear="true"
      :can-deselect="false"
      :object="true"
      :search-filter="searchBankBranchFilter"
      no-results-text="見つかりませんでした"
      no-options-text="1文字以上入力してください"
      @search-change="aliasSearchBankBranch"
      data-testid="selectedBankBranch"
    />

    <label class="multiselect-label text-mild text-size-14 mr-1 mt-3">
      口座種別
    </label>
    <p>
      ※普通預金のみ設定可能です
    </p>

    <ValidationProvider
      v-slot="validationState"
      name="口座番号"
      :rules="{ required: true, min_account_number: 7, max: 7 }"
      vid="accountNumber"
      v-model="inputValues.accountNumber"
    >
      <TextFieldGroup
        v-model="inputValues.accountNumber"
        label="口座番号"
        appLabelText="必須"
        placeholder="1000000"
        inputmode="numeric"
        :validationState="validationState"
        class="mt-3"
        data-testid="accountNumber"
      />
      <p class="text-size-14 text-gray-05">7桁までの数字</p>
    </ValidationProvider>

    <TextFieldGroup
      :value="holderName"
      label="口座名義"
      placeholder=""
      class="mt-3"
      :disabled="true"
    />
    <p class="text-size-14 text-gray-05">
      ※口座名義はアカウントの基本情報から取得しています
    </p>

    <dt class="mt-4 text-weight-500">報酬の受取について</dt>
    <dd>
      <RewardDescription />
    </dd>
    <slot name="footerButton" />
  </BForm>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from "vue-facing-decorator";
import Multiselect from "@vueform/multiselect";
import AppLabel from "@/components/atoms/input/AppLabel.vue";
import Header from "@/components/organisms/common/Header.vue";
import TextFieldGroup from "@/components/molecules/form/TextFieldGroup.vue";
import { BankAccountQueueBody } from "@/types/bank_account/BankAccountQueueBody";
import { Bank } from "@/types/Master";
import RewardDescription from "@/components/organisms/order/RewardDescription.vue";
import FormRadioGroup from "@/components/molecules/form/FormRadioGroup.vue";
import { BankBranches } from "@/types/system/BankBranches";
import { systemService } from "@/app/services/SystemService";
import { authStore } from "@/store/auth";

type BankBranchChoice = {
  id: number;
  code: string;
  name: string;
  actualName: string;
  aliases: string[];
};

@Component({
  components: {
    FormRadioGroup,
    AppLabel,
    Header,
    Multiselect,
    TextFieldGroup,
    RewardDescription
  }
})
export default class SetBankForm extends Vue {
  @Prop({
    type: Object,
    required: true
  })
  value!: BankAccountQueueBody;

  @Prop({
    type: Array,
    required: true
  })
  banks!: Bank[];

  previouslyBankChosen = false;
  bankBranches: BankBranchChoice[] = [];
  searchedBranches: BankBranchChoice[] = [];
  selectedBranch: BankBranchChoice | null = null;

  get inputValues() {
    return this.value;
  }

  set inputValues(v) {
    if (this.value !== v) this.$emit("input", v);
  }

  get holderName() {
    const user = authStore.user;
    return (
      this.inputValues.holderName ||
      // TODO: そもそもここで口座名義を表示する必要はないのでは
      // 口座名義の生成ロジックがサーバサイドとクライアントサイドで重複しているので修正すべき
      user?.lastNameKana + " " + user?.firstNameKana
    );
  }

  searchFilter(option: Bank, query: string) {
    if (query.length === 0) {
      return false;
    }

    const normalizedQuery = this.normalize(query);
    const normalizedBankName = this.normalize(option.name);

    const isBankNameMatch = normalizedBankName.includes(normalizedQuery);
    const isAliasesMatch =
      option.aliases &&
      option.aliases.some(alias => {
        const normalizedBankAlias = this.normalize(alias);
        return normalizedBankAlias.includes(normalizedQuery);
      });

    return isBankNameMatch || isAliasesMatch;
  }

  aliasSearch(query: string) {
    if (query.length >= 1) {
      this.inputValues.searchedBanks = this.banks.filter(bank =>
        this.searchFilter(bank, query)
      );
    } else if (this.inputValues.selectedBank) {
      this.inputValues.searchedBanks = [this.inputValues.selectedBank];
    } else {
      this.inputValues.searchedBanks = [];
    }
  }

  /**
   * 下記の正規化を行う
   * - 大文字を小文字に変換
   * - 全角文字を半角文字へ変換
   * - 区切り文字、記号、句読点を全て除去
   *
   * @param {string} str
   * @return {string}
   */
  normalize(str: string) {
    return str
      .toLowerCase()
      .normalize("NFKC")
      .replace(/[\p{S}\p{Z}\p{P}]/giu, "");
  }

  @Watch("inputValues.selectedBank")
  async onChangeInputValuesSelectedBank(bank: Bank) {
    const bankCode = this.inputValues.selectedBank;
    if (bankCode === null) {
      this.bankBranches = [];
      this.previouslyBankChosen = true;
      return;
    }

    this.bankBranches = this.convertBankBranchesToChoices(
      await systemService.fetchMasterBankBranches(bankCode.code)
    );

    if (this.previouslyBankChosen) {
      // viewに遷移してきて一度でも銀行が選択されていた (つまり銀行が切り替えられた) 場合
      // 支店の情報を保持していると不整合が起こるのでクリアする。
      this.selectedBranch = null;
    } else {
      // viewに遷移してきて一度も銀行が選択されていない (つまり過去に保存されていた銀行情報に基く初期化時) 場合
      // 現存する情報をもとにselectedBranchを補完する。
      const matchedBranch = this.bankBranches.find(
        branch => branch.code === this.inputValues.branchCode
      );
      this.selectedBranch = matchedBranch || null;
    }

    this.previouslyBankChosen = true;
  }

  searchBankBranchFilter(option: BankBranchChoice, query: string) {
    if (query.length === 0) {
      return false;
    }

    const normalizedQuery = this.normalize(query);

    const isCodeMatch = option.code.includes(query);
    const isAliasesMatch =
      option.aliases &&
      option.aliases.some(alias => {
        const normalizedBankAlias = this.normalize(alias);
        return normalizedBankAlias.includes(normalizedQuery);
      });
    return isCodeMatch || isAliasesMatch;
  }

  aliasSearchBankBranch(query: string) {
    if (query.length >= 1) {
      this.searchedBranches = this.bankBranches.filter(branch =>
        this.searchBankBranchFilter(branch, query)
      );
    } else if (this.selectedBranch) {
      this.searchedBranches = [this.selectedBranch];
    } else {
      this.searchedBranches = [];
    }
  }

  @Watch("selectedBranch")
  onChangeSelectedBranch(branch: BankBranchChoice | null) {
    this.inputValues.branchCode = branch?.code || "";
    this.inputValues.branchName = branch?.actualName || "";
  }

  private convertBankBranchesToChoices(
    bankBranches: BankBranches
  ): BankBranchChoice[] {
    const choices: BankBranchChoice[] = [];
    for (const code in bankBranches) {
      const branch = bankBranches[code];
      choices.push({
        id: parseInt(code, 10),
        code: code,
        name: `${branch.name} (コード: ${code})`,
        actualName: branch.name,
        aliases: [branch.name, branch.kana, branch.hiragana, branch.roma]
      });
    }
    return choices;
  }
}
</script>

<style lang="scss" scoped>
.set-bank {
  .multiselect-label {
    margin-bottom: 6px;
  }
}

:deep(.multiselect__content) {
  max-width: 100%;
}

.branch-name-suffix {
  margin-top: 32px;
  line-height: 40px;
  white-space: nowrap;
}

.bank-branch-select {
  margin-top: 12px;
}
</style>
<style src="@vueform/multiselect/themes/default.css"></style>
